import { EFinancingProgram } from '@if/core/types'
import { CircularProgress, Stack, debounce } from '@mui/material'
import {
  useSetApplicantCustomerReference,
  useSetCoapplicantCustomerReference,
} from '@src/data/api/customer-reference/customer-reference-api'
import { useFindCustomers } from '@src/data/api/loan-customer/loan-customer-api'
import { ChangeEvent, FocusEvent, memo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActionsDialog, InputTextField } from '../../../components'
import { Applicant } from '../../../data/types'
import { SetCustomerReferenceDto } from '../../../data/types/CustomerReferenceSchema'
import { LoanCustomer, SearchCustomerDto } from '../../../data/types/LoanCustomerSchema'
import { reportErrorToConsole } from '../../../services/error-logger'
import LoanCustomers from './loanCustomers'

interface Props {
  applicant: Applicant
  onCancel: () => void
  open: boolean
  title: string
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  isApplicant: boolean
}

const CustomerAccountDialog = ({
  applicant,
  onCancel,
  open,
  title,
  creditApplicationId,
  financingProgramId,
  isApplicant,
}: Props) => {
  const { t } = useTranslation()
  const [setApplicantCustomerReference] = useSetApplicantCustomerReference()
  const [setCoapplicantCustomerReference] = useSetCoapplicantCustomerReference()
  const [findCustomers] = useFindCustomers()

  const [selectedCustomer, setSelectedCustomer] = useState<string>(
    applicant?.existingCustomerReference ? applicant?.existingCustomerReference.customerId : '',
  )
  const [customers, setCustomers] = useState<LoanCustomer[]>([])
  const [firstName, setFirstName] = useState<string>(applicant.firstName)
  const [lastName, setLastName] = useState<string>(applicant.lastName)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleSelectedCustomer = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setSelectedCustomer(value)
  }, [])

  const onConfirmData = useCallback(async () => {
    const selectedLoanCustomer = customers.find((x) => x.id === selectedCustomer)
    const selectedData: SetCustomerReferenceDto = {
      creditApplicationId,
      customerId: selectedLoanCustomer?.id ?? '',
      financingProgramId,
    }

    const mutate = isApplicant ? setApplicantCustomerReference : setCoapplicantCustomerReference
    try {
      await mutate(selectedData)
      onCancel()
    } catch (error) {
      reportErrorToConsole(error)
    }
  }, [
    customers,
    selectedCustomer,
    onCancel,
    creditApplicationId,
    financingProgramId,
    isApplicant,
    setApplicantCustomerReference,
    setCoapplicantCustomerReference,
  ])

  useEffect(() => {
    setFirstName(applicant.firstName)
    setLastName(applicant.lastName)
  }, [applicant])

  useEffect(() => {
    if (open) {
      const searchCustomer = {
        firstName,
        lastName,
        birthDate: applicant.birthDate,
        sin: applicant.sin,
      } as SearchCustomerDto
      setIsLoading(true)
      const fetchCustomers = async () => {
        try {
          const data = await findCustomers(searchCustomer)
          setCustomers(data)
        } catch (error) {
          reportErrorToConsole(error)
        } finally {
          setIsLoading(false)
        }
      }
      fetchCustomers().catch(reportErrorToConsole)
    }
  }, [applicant, findCustomers, firstName, lastName, open])

  const onFirstNameEdited = useCallback((event: FocusEvent<HTMLInputElement>) => {
    setFirstName(event.target.value)
  }, [])

  const onLastNameEdited = useCallback((event: FocusEvent<HTMLInputElement>) => {
    setLastName(event.target.value)
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFirstNameEdited = useCallback(debounce(onFirstNameEdited, 500), [onFirstNameEdited])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceLastNameEdited = useCallback(debounce(onLastNameEdited, 500), [onLastNameEdited])

  return (
    <ActionsDialog onCancel={onCancel} onConfirm={onConfirmData} open={open} title={title}>
      <Stack spacing={2} direction="row" marginTop={2} justifyContent="center">
        <InputTextField
          sx={{ width: '20rem' }}
          label={t('editCreditApplication.information.firstName')}
          defaultValue={firstName}
          onChange={debounceFirstNameEdited}
        />
        <InputTextField
          sx={{ width: '20rem' }}
          label={t('editCreditApplication.information.lastName')}
          defaultValue={lastName}
          onChange={debounceLastNameEdited}
        />
      </Stack>
      {isLoading ? (
        <Stack justifyContent="center" alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <LoanCustomers
          customers={customers}
          handleSelectedCustomer={handleSelectedCustomer}
          selectedCustomer={selectedCustomer}
        />
      )}
    </ActionsDialog>
  )
}

export default memo(CustomerAccountDialog)
